/**
 * Copyright IBM Corp. 2021
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */
import Hammer from "hammerjs";
import throttle from "lodash/throttle";
import TriFloorPlanUtils from "../util/TriFloorPlanUtils.js";
import TriFloorPlanPlugin from "./TriFloorPlanPlugin.js";
import TriSelectablePlugin from "./TriSelectablePlugin.js"

export default class TriPanPlugin extends TriFloorPlanPlugin {
  static type = "pan";

  constructor() {
    super();
    this.hammer = null;
    this.panStart = null;
  }

  pluginDidUpdate() {
    const { floorPlanLoaded, enabled } = this.props;
    if (!floorPlanLoaded || !enabled) {
      this.stop();
      return;
    }
    this.run();
  }

  pluginDidRemove() {
    this.stop();
  }

  createHammerManager(svgElement) {
    this.hammer = new Hammer.Manager(svgElement, {
      recognizers: [[Hammer.Pan]],
    });

    this.hammer.on("panstart", this.onPanStart.bind(this));
    this.hammer.on("panmove", this.onPanMove.bind(this));
    this.hammer.on("panend", this.onPanEnd.bind(this));
  }

  run() {
    const { svgElement } = this.props.triFloorPlan;
    if (this.hammer === null) {
      this.createHammerManager(svgElement);
    } else if (this.hammer.element !== svgElement) {
      this.hammer.destroy();
      this.createHammerManager(svgElement);
    }
  }

  stop() {
    if (this.hammer !== null) {
      this.hammer.destroy();
      this.hammer = null;
    }
  }

  onPanStart( e )
  {
    if( !this.enabled ) return;

    // Disable selection because the pan results in a tap at the end.
    const { triFloorPlan } = this.props;
    triFloorPlan.pluginInstances.forEach( ( instance ) =>
    {
      if( instance.props.type === TriSelectablePlugin.type ) instance.enabled = false;
    } );

    const ctm = this.props.triFloorPlan.svgElement.getScreenCTM();
    const matrix = ctm.inverse();
    this.panStart = {
      matrix,
      orig: {
        x: this.props.triFloorPlan.svgViewBox.x,
        y: this.props.triFloorPlan.svgViewBox.y,
      },
    };
    this.panFloorPlan(e.deltaX, e.deltaY, this.panStart);
  }

  onPanMove(e) {
    if (this.panStart === null || !this.enabled ) {
      return;
    }

    const { x, y, height, width } = this.props.triFloorPlan.svgViewPort;
    if (
      e.center.x >= x &&
      e.center.x <= x + width &&
      e.center.y >= y &&
      e.center.y <= y + height
    ) {
      this.panFloorPlan(e.deltaX, e.deltaY, this.panStart);
    }
  }

  panFloorPlan = throttle((deltaX, deltaY, panStart) => {
    const { svgViewBox, svgElement } = this.props.triFloorPlan;
    this.props.triFloorPlan.svgViewBox = TriFloorPlanUtils.panSvgViewBox(
      svgElement,
      svgViewBox,
      deltaX,
      deltaY,
      panStart
    );
  }, 10);

  onPanEnd() {
    const { triFloorPlan } = this.props;
    triFloorPlan.pluginInstances.forEach( ( instance ) =>
    {
      if( instance.props.type === TriSelectablePlugin.type )
      {
        setTimeout( () => { instance.enabled = true; }, 100 );
      }
    } );

    this.panStart = null;
  }
}
